import { useTranslation } from "react-i18next";import React from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/shared/utils';

/**
 * Sortable Table Header Component
 * Use this for any table column that needs sorting functionality
 */
export const SortableHeader = ({
  column,
  label,
  sortConfig,
  onSort,
  className,
  align = 'left'
}) => {const { t } = useTranslation("common");
  const isSorted = sortConfig?.key === column;
  const direction = isSorted ? sortConfig.direction : null;

  return (
    <th
      className={cn(
        "py-3 px-4 font-medium cursor-pointer hover:bg-muted/50 transition-colors select-none",
        align === 'right' && "text-right",
        align === 'center' && "text-center",
        className
      )}
      onClick={() => onSort(column)}
      data-testid={`sort-header-${column}`}>
      
      <div className={cn(
        "flex items-center gap-1",
        align === 'right' && "justify-end",
        align === 'center' && "justify-center"
      )}>
        <span>{label}</span>
        <span className="text-muted-foreground">
          {isSorted ?
          direction === 'asc' ?
          <ChevronUp className="h-4 w-4" /> :

          <ChevronDown className="h-4 w-4" /> :


          <ChevronsUpDown className="h-4 w-4 opacity-50" />
          }
        </span>
      </div>
    </th>);

};

/**
 * Hook for managing sort state
 * Returns sortConfig and handler function
 */
export const useSortableData = (initialData, initialSort = null) => {
  const [sortConfig, setSortConfig] = React.useState(initialSort);

  const sortedData = React.useMemo(() => {
    if (!initialData || !Array.isArray(initialData)) return initialData || [];
    if (!sortConfig) return initialData;

    const sorted = [...initialData].sort((a, b) => {
      const aValue = getNestedValue(a, sortConfig.key);
      const bValue = getNestedValue(b, sortConfig.key);

      // Handle null/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle dates
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortConfig.direction === 'asc' ?
        aValue - bValue :
        bValue - aValue;
      }

      // Handle date strings
      if (isDateString(aValue) && isDateString(bValue)) {
        const dateA = new Date(aValue);
        const dateB = new Date(bValue);
        return sortConfig.direction === 'asc' ?
        dateA - dateB :
        dateB - dateA;
      }

      // Handle numbers
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ?
        aValue - bValue :
        bValue - aValue;
      }

      // Handle strings
      const strA = String(aValue).toLowerCase();
      const strB = String(bValue).toLowerCase();

      if (sortConfig.direction === 'asc') {
        return strA.localeCompare(strB);
      }
      return strB.localeCompare(strA);
    });

    return sorted;
  }, [initialData, sortConfig]);

  const requestSort = (key) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        // Toggle direction or clear
        if (prev.direction === 'asc') {
          return { key, direction: 'desc' };
        }
        return null; // Clear sort on third click
      }
      return { key, direction: 'asc' };
    });
  };

  return { sortedData, sortConfig, requestSort };
};

// Helper to get nested object values like "user.name"
const getNestedValue = (obj, path) => {
  if (!path) return obj;
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
};

// Check if string looks like a date
const isDateString = (value) => {
  if (typeof value !== 'string') return false;
  const date = new Date(value);
  return !isNaN(date.getTime()) && value.includes('-') || value.includes('/');
};

/**
 * TableHead component with built-in sort support
 */
export const SortableTableHead = ({ columns, sortConfig, onSort }) =>
<thead className="bg-muted/50 border-b">
    <tr>
      {columns.map((col) =>
    <SortableHeader
      key={col.key}
      column={col.key}
      label={col.label}
      sortConfig={sortConfig}
      onSort={onSort}
      align={col.align}
      className={col.className} />

    )}
    </tr>
  </thead>;


export default { SortableHeader, useSortableData, SortableTableHead };